import React, { useState, useEffect } from 'react';
import { SkewButton } from '../components/UIComponents';
import { Target, Check, Calendar, TrendingUp, MessageCircle, Star, Zap, Heart, X, Send, Clock, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAIN_LOGO = "https://customer-assets.emergentagent.com/job_goalgeous-hub/artifacts/ad5dhro0_Untitled%20design.png";

const iconMap = {
  Calendar, TrendingUp, Target, Star, MessageCircle, Zap
};

// Default content (fallback if API fails)
const defaultContent = {
  hero: {
    title: "ACCOUNTABILITY",
    titleAccent: "CHATS",
    tagline: "Discipline. Structure.",
    taglineAccent: "Results.",
    description: "This is where the real change happens. The Accountability Chats is designed to help women stay consistent, focused, and committed to their goals. This is not just fitness. This is structure, guidance, and support that keeps you locked in.",
    ctaText: "View Current Program"
  },
  features: {
    sectionLabel: "What's Included",
    sectionTitle: "Your",
    sectionTitleAccent: "Transformation",
    sectionTitleSuffix: "System",
    items: [
      { text: "Daily accountability check-ins" },
      { text: "Progress tracking" },
      { text: "Nutrition habit review" },
      { text: "Goal setting and refocusing" },
      { text: "Support, structure, and real conversations" },
      { text: "A system that helps you stay disciplined and consistent" }
    ]
  },
  chooseYou: {
    badge: "NOW ENROLLING",
    title: "CHOOSE YOU.",
    titleAccent: "A 30-DAY SERIES",
    startDate: "Starts March 2",
    duration: "30 Days",
    descriptionIntro: "Choose You is a 30-day accountability mastermind reset.",
    descriptionPoints: [
      "This is for the person who keeps saying \"I'll start Monday.\"",
      "This is for the person already in the gym who wants to level up their discipline.",
      "This is the highest level of accountability and surveillance — held with love."
    ],
    descriptionOutro: "This program is designed to teach you how to keep your word with yourself.",
    motto: "No excuses. No hiding. No negotiating.",
    values: "Just ownership. Consistency. Growth."
  },
  pricing: {
    chatOnly: {
      price: "$100",
      title: "Chat Only",
      description: "For those already committed to a gym",
      buttonText: "Apply for Choose You"
    },
    chatWorkouts: {
      price: "$169",
      title: "Chat + Unlimited Workouts",
      description: "For those not currently committed to a gym",
      buttonText: "Apply for Choose You"
    }
  },
  payment: {
    title: "Payment Methods",
    cashApp: "$PowerUpYourFitness",
    zelle: "PowerUpYourFitness@gmail.com",
    proofNote: "Proof of payment must be emailed to PowerUpYourFitness@gmail.com"
  },
  upcoming: {
    sectionLabel: "Upcoming Accountability Chats (Enrollment Closed)",
    title: "Future Programs",
    description: "Stay tuned for upcoming accountability chat dates. Follow us to be the first to know when enrollment opens!",
    programs: [
      { name: "21-Day Reset", status: "Coming Soon" },
      { name: "40-Day Discipline", status: "Coming Soon" },
      { name: "6-Week Lock-In", status: "Coming Soon" }
    ]
  },
  cta: {
    title: "Choose",
    titleAccent: "You",
    description: "Your transformation starts with accountability. Are you ready to commit?",
    motto: "Ownership. Consistency.",
    mottoAccent: "Growth.",
    buttonText: "Apply for Choose You"
  },
  form: {
    title: "Apply for",
    titleAccent: "Choose You",
    subtitle: "30-Day Series - Starts March 2",
    submitText: "Apply for Choose You",
    confirmationTitle: "Application",
    confirmationTitleAccent: "Received!",
    confirmationMessage: "Your spot is reserved pending payment. Pay via Cash App or Zelle using the details below. Include your full name and \"Choose You\" in the notes. Your spot is confirmed once payment is received."
  }
};

// Choose You Signup Form Component
const ChooseYouSignupForm = ({ isOpen, onClose, preselectedOption, content, paymentContent, formSettings }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    optionSelected: preselectedOption || '',
    notes: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);

  // Use form settings if available, otherwise use content prop
  const formContent = formSettings || content;
  const paymentInfo = formSettings?.payment_info || paymentContent;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/forms/choose-you-intake/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setIsSubmitted(true);
      } else {
        setSubmissionError('Failed to submit. Please try again.');
      }
    } catch (error) {
      setSubmissionError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      optionSelected: '',
      notes: ''
    });
    setIsSubmitted(false);
    setSubmissionError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-2xl bg-brand-dark border-2 border-brand-lime/30 my-8">
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-brand-gray hover:text-white hover:bg-brand-lime/20 transition-colors rounded-full"
          data-testid="close-form-btn"
        >
          <X size={24} />
        </button>

        <div className="p-6 md:p-10">
          {!isSubmitted ? (
            <>
              <div className="text-center mb-8">
                <Heart size={40} className="text-brand-lime mx-auto mb-4" />
                <h3 className="font-oswald text-3xl md:text-4xl uppercase text-white mb-2">
                  {formContent.form_title || formContent.title} <span className="text-brand-lime">{formContent.form_title_accent || formContent.titleAccent}</span>
                </h3>
                <p className="font-montserrat text-brand-gray">
                  {formContent.form_subtitle || formContent.subtitle}
                </p>
              </div>

              {submissionError && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30">
                  <p className="text-red-400 font-montserrat text-sm">{submissionError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="choose-you-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-oswald text-sm uppercase tracking-wider text-brand-lime mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full bg-brand-black border border-white/20 text-white px-4 py-3 font-montserrat focus:border-brand-lime focus:outline-none transition-colors"
                      placeholder="Enter first name"
                      data-testid="input-first-name"
                    />
                  </div>
                  <div>
                    <label className="block font-oswald text-sm uppercase tracking-wider text-brand-lime mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full bg-brand-black border border-white/20 text-white px-4 py-3 font-montserrat focus:border-brand-lime focus:outline-none transition-colors"
                      placeholder="Enter last name"
                      data-testid="input-last-name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-oswald text-sm uppercase tracking-wider text-brand-lime mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-brand-black border border-white/20 text-white px-4 py-3 font-montserrat focus:border-brand-lime focus:outline-none transition-colors"
                    placeholder="Enter email address"
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <label className="block font-oswald text-sm uppercase tracking-wider text-brand-lime mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full bg-brand-black border border-white/20 text-white px-4 py-3 font-montserrat focus:border-brand-lime focus:outline-none transition-colors"
                    placeholder="Enter phone number"
                    data-testid="input-phone"
                  />
                </div>

                <div>
                  <label className="block font-oswald text-sm uppercase tracking-wider text-brand-lime mb-2">
                    Option Selected *
                  </label>
                  <select
                    name="optionSelected"
                    value={formData.optionSelected}
                    onChange={handleChange}
                    required
                    className="w-full bg-brand-black border border-white/20 text-white px-4 py-3 font-montserrat focus:border-brand-lime focus:outline-none transition-colors cursor-pointer"
                    data-testid="select-option"
                  >
                    <option value="">Select an option</option>
                    <option value="chat-only">Chat Only - $100 (Already committed to a gym)</option>
                    <option value="chat-workouts">Chat + Unlimited Workouts - $169 (Not currently committed to a gym)</option>
                  </select>
                </div>

                <div>
                  <label className="block font-oswald text-sm uppercase tracking-wider text-brand-lime mb-2">
                    Notes <span className="text-brand-gray">(optional)</span>
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    className="w-full bg-brand-black border border-white/20 text-white px-4 py-3 font-montserrat focus:border-brand-lime focus:outline-none transition-colors resize-none"
                    placeholder="Any additional notes or questions..."
                    data-testid="input-notes"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-brand-lime text-brand-black px-8 py-4 font-oswald uppercase tracking-wider text-lg transform -skew-x-6 hover:bg-brand-pink hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                  data-testid="submit-form-btn"
                >
                  <span className="skew-x-6 flex items-center gap-2">
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        {formContent.submit_button_text || formContent.submitText}
                      </>
                    )}
                  </span>
                </button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="confirmation-message">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-lime/20 flex items-center justify-center">
                <Check size={40} className="text-brand-lime" />
              </div>
              
              <h3 className="font-oswald text-3xl uppercase text-white mb-4">
                {formContent.confirmation_title || formContent.confirmationTitle} <span className="text-brand-lime">{formContent.confirmation_title_accent || formContent.confirmationTitleAccent}</span>
              </h3>
              
              <div className="bg-brand-black border border-brand-lime/30 p-6 mb-6 text-left">
                <p className="font-montserrat text-white leading-relaxed mb-4">
                  {formContent.confirmation_message || formContent.confirmationMessage}
                </p>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="bg-brand-dark border border-brand-lime/20 p-4">
                  <p className="font-oswald text-sm uppercase tracking-wider text-brand-gray mb-1">Cash App</p>
                  <p className="font-oswald text-2xl text-brand-lime">{paymentInfo.cash_app || paymentInfo.cashApp}</p>
                </div>
                <div className="bg-brand-dark border border-brand-lime/20 p-4">
                  <p className="font-oswald text-sm uppercase tracking-wider text-brand-gray mb-1">Zelle</p>
                  <p className="font-oswald text-xl text-brand-lime">{paymentInfo.zelle}</p>
                </div>
              </div>
              
              <div className="bg-brand-pink/10 border border-brand-pink/30 p-4 mb-6">
                <p className="font-montserrat text-sm text-brand-gray">
                  {paymentInfo.proof_note || paymentInfo.proofNote}
                </p>
              </div>
              
              <button
                onClick={handleClose}
                className="bg-brand-lime/10 text-brand-lime px-8 py-3 font-oswald uppercase tracking-wider border border-brand-lime/30 hover:bg-brand-lime hover:text-brand-black transition-all duration-300"
                data-testid="close-confirmation-btn"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const AccountabilityCheckPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [preselectedOption, setPreselectedOption] = useState('');
  const [content, setContent] = useState(defaultContent);
  const [formSettings, setFormSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchContent();
    fetchFormSettings();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/accountability-chats`);
      if (response.ok) {
        const data = await response.json();
        if (data && data.content) {
          setContent(data.content);
        }
      }
    } catch (error) {
      console.log('Using default content');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFormSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/forms/choose-you-intake/settings`);
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setFormSettings(data);
        }
      }
    } catch (error) {
      console.log('Using default form settings');
    }
  };

  const openFormWithOption = (optionId) => {
    setPreselectedOption(optionId);
    setIsFormOpen(true);
  };

  if (isLoading) {
    return (
      <div className="bg-brand-black min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-brand-lime animate-spin" />
      </div>
    );
  }

  const { hero, features, chooseYou, pricing, payment, upcoming, cta, form } = content;

  return (
    <div data-testid="accountability-page" className="bg-brand-black pt-20">
      <ChooseYouSignupForm 
        isOpen={isFormOpen} 
        onClose={() => setIsFormOpen(false)}
        preselectedOption={preselectedOption}
        content={form}
        formSettings={formSettings}
        paymentContent={payment}
      />

      {/* Hero Section */}
      <section data-testid="accountability-hero" className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-black via-brand-dark to-brand-black" />
        <div className="absolute top-20 left-0 w-96 h-96 bg-brand-lime/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-0 w-96 h-96 bg-brand-pink/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <img 
            src={MAIN_LOGO} 
            alt="Power Up Your Fitness" 
            className="h-28 mx-auto mb-10"
          />
          
          <h1 className="font-oswald text-5xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tighter text-white mb-6">
            {hero.title} <span className="text-brand-lime">{hero.titleAccent}</span>
          </h1>
          
          <div className="w-48 h-1 bg-gradient-to-r from-brand-lime via-brand-pink to-brand-lime mx-auto mb-8" />
          
          <p className="font-oswald text-2xl md:text-3xl uppercase tracking-[0.2em] text-brand-gray mb-8">
            {hero.tagline} <span className="text-brand-lime">{hero.taglineAccent}</span>
          </p>
          
          <p className="font-montserrat text-lg md:text-xl text-brand-gray leading-relaxed max-w-3xl mx-auto mb-10">
            {hero.description}
          </p>
          
          <SkewButton href="#choose-you" testId="accountability-cta">
            {hero.ctaText}
          </SkewButton>
        </div>
      </section>

      {/* What's Included Section */}
      <section data-testid="whats-included" className="py-20 bg-brand-dark">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="inline-block bg-brand-lime/20 px-6 py-2 rounded-full mb-4">
              <span className="font-oswald text-sm uppercase tracking-[0.2em] text-brand-lime">
                {features.sectionLabel}
              </span>
            </span>
            <h2 className="font-oswald text-4xl md:text-5xl uppercase text-white">
              {features.sectionTitle} <span className="text-brand-lime">{features.sectionTitleAccent}</span> {features.sectionTitleSuffix}
            </h2>
          </div>
          
          <div className="bg-brand-black border-2 border-brand-lime/30 p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.items.map((feature, index) => {
                const icons = [Calendar, TrendingUp, Target, Star, MessageCircle, Zap];
                const IconComponent = icons[index % icons.length];
                return (
                  <div 
                    key={index}
                    className="flex items-center gap-4 p-4 bg-brand-dark/50 border border-white/10 hover:border-brand-lime/30 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-full bg-brand-lime/10 flex items-center justify-center flex-shrink-0">
                      <IconComponent size={24} className="text-brand-lime" />
                    </div>
                    <span className="font-montserrat text-white">{feature.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CHOOSE YOU - Main Active Program Section */}
      <section id="choose-you" data-testid="choose-you-section" className="py-24 bg-brand-black relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-brand-lime/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-pink/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 w-full px-6 md:px-12 lg:px-24">
          {/* Section Header */}
          <div className="max-w-5xl mx-auto text-center mb-12">
            <span className="inline-block bg-brand-lime text-brand-black px-6 py-2 mb-6">
              <span className="font-oswald text-sm uppercase tracking-[0.2em]">
                {chooseYou.badge}
              </span>
            </span>
            
            <h2 className="font-oswald text-4xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tighter text-white mb-4">
              {chooseYou.title} <span className="text-brand-lime">{chooseYou.titleAccent}</span>
            </h2>
            
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 text-brand-lime">
                <Calendar size={20} />
                <span className="font-oswald uppercase tracking-wider">{chooseYou.startDate}</span>
              </div>
              <span className="text-brand-gray">|</span>
              <div className="flex items-center gap-2 text-brand-gray">
                <Clock size={20} />
                <span className="font-oswald uppercase tracking-wider">{chooseYou.duration}</span>
              </div>
            </div>
            
            <div className="w-32 h-1 bg-gradient-to-r from-brand-lime to-brand-pink mx-auto mb-10" />
          </div>

          {/* Description */}
          <div className="max-w-3xl mx-auto mb-16">
            <div className="bg-brand-dark border border-brand-lime/30 p-8 md:p-10">
              <p className="font-montserrat text-xl text-white leading-relaxed mb-6">
                <span className="text-brand-lime font-semibold">{chooseYou.descriptionIntro}</span>
              </p>
              
              <div className="space-y-4 text-brand-gray font-montserrat leading-relaxed">
                {chooseYou.descriptionPoints.map((point, index) => (
                  <p key={index} dangerouslySetInnerHTML={{ __html: point.replace(/"([^"]+)"/g, '<span class="text-white">"$1"</span>').replace(/level up their discipline/g, '<span class="text-brand-lime">level up their discipline</span>').replace(/accountability and surveillance/g, '<span class="text-brand-pink">accountability and surveillance</span>') }} />
                ))}
              </div>
              
              <div className="border-t border-white/10 mt-8 pt-8">
                <p className="font-montserrat text-white leading-relaxed mb-4">
                  {chooseYou.descriptionOutro.split('keep your word with yourself').map((part, i) => 
                    i === 0 ? <span key={i}>{part}<span className="text-brand-lime font-semibold">keep your word with yourself</span></span> : part
                  )}
                </p>
                <p className="font-oswald text-xl uppercase tracking-wider text-brand-gray">
                  {chooseYou.motto}
                </p>
                <p className="font-oswald text-2xl uppercase tracking-wider text-white mt-4">
                  {chooseYou.values.split('.').map((val, i) => {
                    const trimmed = val.trim();
                    if (!trimmed) return null;
                    const colors = ['text-brand-lime', 'text-brand-pink', 'text-brand-lime'];
                    return <span key={i}>{i > 0 ? '. ' : ''}<span className={colors[i % 3]}>{trimmed}</span></span>;
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Chat Only */}
            <div 
              data-testid="choose-you-card-chat-only"
              className="bg-brand-dark border-2 border-brand-lime/30 p-8 hover:border-brand-lime transition-all duration-300"
            >
              <div className="text-center">
                <div className="mb-6">
                  <span className="font-oswald text-5xl md:text-6xl text-brand-lime">{pricing.chatOnly.price}</span>
                </div>
                
                <h3 className="font-oswald text-xl md:text-2xl uppercase tracking-tight text-white mb-4">
                  {pricing.chatOnly.title}
                </h3>
                
                <p className="font-montserrat text-brand-gray mb-8">
                  {pricing.chatOnly.description}
                </p>
                
                <button
                  onClick={() => openFormWithOption('chat-only')}
                  data-testid="apply-chat-only"
                  className="w-full bg-brand-lime text-brand-black px-8 py-4 font-oswald uppercase tracking-wider transform -skew-x-6 hover:bg-brand-pink hover:text-white transition-all duration-300 flex items-center justify-center"
                >
                  <span className="skew-x-6">{pricing.chatOnly.buttonText}</span>
                </button>
              </div>
            </div>

            {/* Chat + Workouts */}
            <div 
              data-testid="choose-you-card-chat-workouts"
              className="bg-brand-dark border-2 border-brand-lime/30 p-8 hover:border-brand-lime transition-all duration-300"
            >
              <div className="text-center">
                <div className="mb-6">
                  <span className="font-oswald text-5xl md:text-6xl text-brand-lime">{pricing.chatWorkouts.price}</span>
                </div>
                
                <h3 className="font-oswald text-xl md:text-2xl uppercase tracking-tight text-white mb-4">
                  {pricing.chatWorkouts.title}
                </h3>
                
                <p className="font-montserrat text-brand-gray mb-8">
                  {pricing.chatWorkouts.description}
                </p>
                
                <button
                  onClick={() => openFormWithOption('chat-workouts')}
                  data-testid="apply-chat-workouts"
                  className="w-full bg-brand-lime text-brand-black px-8 py-4 font-oswald uppercase tracking-wider transform -skew-x-6 hover:bg-brand-pink hover:text-white transition-all duration-300 flex items-center justify-center"
                >
                  <span className="skew-x-6">{pricing.chatWorkouts.buttonText}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-brand-black border border-white/10 p-8 text-center">
              <h4 className="font-oswald text-xl uppercase tracking-wider text-white mb-6">
                {payment.title}
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-brand-dark border border-brand-lime/20 p-4">
                  <p className="font-oswald text-sm uppercase tracking-wider text-brand-gray mb-1">Cash App</p>
                  <p className="font-oswald text-xl text-brand-lime">{payment.cashApp}</p>
                </div>
                <div className="bg-brand-dark border border-brand-lime/20 p-4">
                  <p className="font-oswald text-sm uppercase tracking-wider text-brand-gray mb-1">Zelle</p>
                  <p className="font-oswald text-lg text-brand-lime">{payment.zelle}</p>
                </div>
              </div>
              
              <div className="bg-brand-pink/10 border border-brand-pink/30 p-4">
                <p className="font-montserrat text-sm text-brand-gray">
                  {payment.proofNote}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Accountability Chats (Enrollment Closed) */}
      <section data-testid="upcoming-dates" className="py-20 bg-brand-dark">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <span className="inline-block bg-brand-gray/20 px-6 py-2 rounded-full mb-4">
              <span className="font-oswald text-sm uppercase tracking-[0.2em] text-brand-gray">
                {upcoming.sectionLabel}
              </span>
            </span>
          </div>
          
          <div className="bg-brand-black border border-white/10 p-8 md:p-12 text-center">
            <Calendar size={48} className="text-brand-gray mx-auto mb-6" />
            <h4 className="font-oswald text-2xl uppercase text-white mb-4">
              {upcoming.title}
            </h4>
            <p className="font-montserrat text-brand-gray mb-8">
              {upcoming.description}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {upcoming.programs.map((program, index) => (
                <div 
                  key={index}
                  className={`bg-brand-dark border ${index % 2 === 0 ? 'border-brand-lime/20' : 'border-brand-pink/20'} p-4`}
                >
                  <span className="inline-block bg-brand-gray/20 px-3 py-1 mb-2">
                    <span className="font-oswald text-xs uppercase tracking-wider text-brand-gray">{program.status}</span>
                  </span>
                  <p className="font-montserrat text-sm text-white">{program.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="accountability-cta-bottom" className="py-24 bg-brand-black relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-brand-lime/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-brand-pink/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <img 
            src={MAIN_LOGO} 
            alt="Power Up Your Fitness" 
            className="h-24 mx-auto mb-8"
          />
          
          <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase text-white mb-6">
            {cta.title} <span className="text-brand-lime">{cta.titleAccent}</span>
          </h2>
          
          <p className="font-montserrat text-lg text-brand-gray mb-8">
            {cta.description}
          </p>
          
          <p className="font-oswald text-xl uppercase tracking-wider text-white mb-8">
            {cta.motto} <span className="text-brand-lime">{cta.mottoAccent}</span>
          </p>
          
          <button
            onClick={() => openFormWithOption('')}
            data-testid="accountability-final-cta"
            className="inline-flex items-center gap-2 bg-brand-lime text-brand-black px-10 py-4 font-oswald uppercase tracking-wider text-lg transform -skew-x-6 hover:bg-brand-pink hover:text-white transition-all duration-300"
          >
            <span className="skew-x-6">{cta.buttonText}</span>
          </button>
        </div>
      </section>
    </div>
  );
};
